import { z } from '../common/zod-openapi'
import { bzOptional } from '../domain/common-schemas'
import { guidSchema, isoDateStringSchema, timeZoneIdSchema, uscSchema } from './_common'

const LOCATION_SCOPE_ALL = 'ALL'
const LOCATION_SCOPE_SELECTED = 'SELECTED'

export const accountSummaryLocationSelectionSchema = z
  .object({
    locationGuid: guidSchema,
    displayLabel: z.string().min(1).openapi({ example: 'Main Office' }),
  })
  .openapi('AccountSummaryLocationSelection')

export const accountSummaryLocationScopeAllSchema = z
  .object({
    type: z.literal(LOCATION_SCOPE_ALL),
  })
  .openapi('AccountSummaryLocationScopeAll')

export const accountSummaryLocationScopeSelectedSchema = z
  .object({
    type: z.literal(LOCATION_SCOPE_SELECTED),
    locationGuids: z.array(guidSchema).min(1),
  })
  .openapi('AccountSummaryLocationScopeSelected')

export const accountSummaryLocationScopeInputSchema = z
  .discriminatedUnion('type', [accountSummaryLocationScopeAllSchema, accountSummaryLocationScopeSelectedSchema])
  .openapi('AccountSummaryLocationScopeInput')

export const getAccountSummaryPdfLocationOptionsInputSchema = z
  .object({
    accountGuid: guidSchema,
  })
  .openapi('GetAccountSummaryPdfLocationOptionsInput')

export const accountSummaryLocationOptionSchema = z
  .object({
    locationGuid: guidSchema,
    displayLabel: z.string().min(1).openapi({ example: '123 Main St' }),
    hasActiveJob: z.boolean().openapi({ example: true }),
  })
  .openapi('AccountSummaryLocationOption')

export const getAccountSummaryPdfLocationOptionsOutputSchema = z
  .object({
    locations: z.array(accountSummaryLocationOptionSchema),
    defaultScope: accountSummaryLocationScopeInputSchema,
  })
  .openapi('GetAccountSummaryPdfLocationOptionsOutput')

export const downloadAccountSummaryPdfInputSchema = z
  .object({
    accountGuid: guidSchema,
    locationScope: accountSummaryLocationScopeInputSchema,
  })
  .openapi('DownloadAccountSummaryPdfInput')

export const accountSummaryPdfResponseSchema = z
  .object({
    accountSummaryPdfBase64: z.string().openapi({ example: 'base64-encoded-pdf' }),
  })
  .openapi('AccountSummaryPdfResponse')

export const accountSummaryResolvedLocationScopeSchema = z
  .object({
    type: z.enum([LOCATION_SCOPE_ALL, LOCATION_SCOPE_SELECTED]),
    shortLabel: z.string().min(1),
    detailLabel: bzOptional(z.string().min(1)),
    selectedLocationCount: z.number().int().nonnegative(),
    totalLocationCount: z.number().int().nonnegative(),
    selectedLocations: z.array(accountSummaryLocationSelectionSchema),
  })
  .openapi('AccountSummaryResolvedLocationScope')

export const accountSummaryAlertSchema = z
  .object({
    title: z.string().min(1).openapi({ example: 'DO NOT SERVICE' }),
    reason: bzOptional(z.string().min(1).openapi({ example: 'Collections hold' })),
  })
  .openapi('AccountSummaryAlert')

export const accountSummaryNarrativeSchema = z
  .object({
    text: z.string().min(1),
  })
  .openapi('AccountSummaryNarrative')

export const accountSummaryIdentitySchema = z
  .object({
    displayName: z.string().min(1),
    accountType: z.string().min(1),
    customerSince: isoDateStringSchema,
    customerNumber: bzOptional(z.string().min(1)),
    accountManager: bzOptional(z.string().min(1)),
    referenceNumber: bzOptional(z.string().min(1)),
    tags: z.array(z.string()),
    outstandingBalanceUsc: uscSchema,
    overdueBalanceUsc: uscSchema,
  })
  .openapi('AccountSummaryIdentity')

export const accountSummaryContactSchema = z
  .object({
    contactGuid: guidSchema,
    fullName: z.string().min(1),
    phone: bzOptional(z.string().min(1)),
    phoneLabel: bzOptional(z.string().min(1)),
    email: bzOptional(z.string().min(1)),
    notificationPreference: z.string().min(1),
    isPrimary: z.boolean(),
  })
  .openapi('AccountSummaryContact')

export const accountSummaryPinnedNoteSchema = z
  .object({
    noteGuid: guidSchema,
    text: z.string().min(1),
    createdAt: isoDateStringSchema,
    createdByDisplayName: z.string().min(1),
  })
  .openapi('AccountSummaryPinnedNote')

export const accountSummaryNotesSchema = z
  .object({
    sectionLabel: z.string().min(1),
    pinnedNotes: z.array(accountSummaryPinnedNoteSchema),
  })
  .openapi('AccountSummaryNotes')

export const accountSummaryMaintenancePlanSchema = z
  .object({
    maintenancePlanGuid: guidSchema,
    planName: z.string().min(1),
    status: z.string().min(1),
    locationLabel: z.string().min(1),
    yearlyPriceUsc: uscSchema,
    paymentFlow: z.string().min(1),
    visitsRemaining: z.number().int().nonnegative(),
    nextVisitStatus: bzOptional(z.string().min(1)),
    nextVisitDate: bzOptional(isoDateStringSchema),
  })
  .openapi('AccountSummaryMaintenancePlan')

export const accountSummaryMaintenancePlansSectionSchema = z
  .object({
    title: z.string().min(1),
    scopeLabel: z.string().min(1),
    plans: z.array(accountSummaryMaintenancePlanSchema),
    totalPlans: z.number().int().nonnegative(),
    activePlans: z.number().int().nonnegative(),
    yearlyPriceTotalUsc: uscSchema,
    emptyStateCopy: bzOptional(z.string().min(1)),
  })
  .openapi('AccountSummaryMaintenancePlansSection')

export const accountSummaryEquipmentSchema = z
  .object({
    installedEquipmentGuid: guidSchema,
    equipmentType: z.string().min(1),
    manufacturer: bzOptional(z.string().min(1)),
    modelNumber: bzOptional(z.string().min(1)),
    serialNumber: bzOptional(z.string().min(1)),
    installedOn: bzOptional(isoDateStringSchema),
    ageYears: bzOptional(z.number().int().nonnegative()),
    condition: bzOptional(z.string().min(1)),
    manufacturerWarrantyStatus: bzOptional(z.string().min(1)),
    laborWarrantyStatus: bzOptional(z.string().min(1)),
    isDeactivated: z.boolean(),
  })
  .openapi('AccountSummaryEquipment')

export const accountSummaryHvacSystemSchema = z
  .object({
    installedHvacSystemGuid: guidSchema,
    friendlyName: bzOptional(z.string().min(1)),
    filterSize: bzOptional(z.string().min(1)),
    zoningInfo: z.array(z.string()),
    equipment: z.array(accountSummaryEquipmentSchema),
  })
  .openapi('AccountSummaryHvacSystem')

export const accountSummaryLocationSchema = z
  .object({
    locationGuid: guidSchema,
    displayLabel: z.string().min(1),
    fullAddress: z.string().min(1),
    propertyType: bzOptional(z.string().min(1)),
    estimatedSquareFootage: bzOptional(z.number().int().nonnegative()),
    estimatedBuildDate: bzOptional(isoDateStringSchema),
    lastVisitAt: bzOptional(isoDateStringSchema),
    hvacSystems: z.array(accountSummaryHvacSystemSchema),
    otherEquipment: z.array(accountSummaryEquipmentSchema),
    emptyEquipmentCopy: bzOptional(z.string().min(1)),
  })
  .openapi('AccountSummaryLocation')

export const accountSummaryJobCountSchema = z
  .object({
    label: z.string().min(1),
    count: z.number().int().nonnegative(),
  })
  .openapi('AccountSummaryJobCount')

export const accountSummaryRecentJobSchema = z
  .object({
    jobGuid: guidSchema,
    displayId: z.string().min(1),
    typeLabel: z.string().min(1),
    classLabel: z.string().min(1),
    statusLabel: z.string().min(1),
    locationLabel: z.string().min(1),
    occurredAt: isoDateStringSchema,
    invoicedAmountUsc: bzOptional(uscSchema),
  })
  .openapi('AccountSummaryRecentJob')

export const accountSummaryServiceHistorySectionSchema = z
  .object({
    title: z.string().min(1),
    scopeLabel: z.string().min(1),
    totalJobs: z.number().int().nonnegative(),
    jobCountsByClass: z.array(accountSummaryJobCountSchema),
    jobCountsByStatus: z.array(accountSummaryJobCountSchema),
    recentJobs: z.array(accountSummaryRecentJobSchema),
    footerCopy: z.string().min(1),
    emptyStateCopy: bzOptional(z.string().min(1)),
  })
  .openapi('AccountSummaryServiceHistorySection')

export const accountSummaryInvoiceSchema = z
  .object({
    invoiceGuid: guidSchema,
    displayId: z.string().min(1),
    statusLabel: z.string().min(1),
    totalUsc: uscSchema,
    balanceUsc: uscSchema,
    issuedAt: isoDateStringSchema,
    dueAt: bzOptional(isoDateStringSchema),
    jobDisplayId: bzOptional(z.string().min(1)),
    paidViaLabel: bzOptional(z.string().min(1)),
  })
  .openapi('AccountSummaryInvoice')

export const accountSummaryInvoiceHistorySectionSchema = z
  .object({
    title: z.string().min(1),
    scopeLabel: z.string().min(1),
    totalInvoices: z.number().int().nonnegative(),
    lifetimeValueUsc: uscSchema,
    averageInvoiceUsc: uscSchema,
    openAmountUsc: uscSchema,
    overdueAmountUsc: uscSchema,
    recentInvoices: z.array(accountSummaryInvoiceSchema),
    footerCopy: z.string().min(1),
    emptyStateCopy: bzOptional(z.string().min(1)),
  })
  .openapi('AccountSummaryInvoiceHistorySection')

export const accountSummaryPaymentMethodCountSchema = z
  .object({
    label: z.string().min(1),
    count: z.number().int().nonnegative(),
  })
  .openapi('AccountSummaryPaymentMethodCount')

export const accountSummaryPaymentSummarySchema = z
  .object({
    title: z.string().min(1),
    scopeLabel: z.string().min(1),
    lifetimePaidUsc: uscSchema,
    paymentCount: z.number().int().nonnegative(),
    avgDaysToPay: bzOptional(z.number().int().nonnegative()),
    methodsUsed: z.array(accountSummaryPaymentMethodCountSchema),
    lastPaymentAmountUsc: bzOptional(uscSchema),
    lastPaymentMethodLabel: bzOptional(z.string().min(1)),
    lastPaymentAt: bzOptional(isoDateStringSchema),
  })
  .openapi('AccountSummaryPaymentSummary')

export const accountSummaryTimelineMilestoneSchema = z
  .object({
    label: z.string().min(1),
    occurredAt: isoDateStringSchema,
    detail: z.string().min(1),
    accountWide: z.boolean(),
  })
  .openapi('AccountSummaryTimelineMilestone')

export const accountSummaryTimelineSectionSchema = z
  .object({
    title: z.string().min(1),
    scopeLabel: z.string().min(1),
    milestones: z.array(accountSummaryTimelineMilestoneSchema),
  })
  .openapi('AccountSummaryTimelineSection')

export const CreateAccountSummaryPdfBodySchema = z
  .object({
    accountGuid: guidSchema,
    companyGuid: guidSchema,
    displayName: z.string().min(1),
    generatedAt: isoDateStringSchema,
    tzId: timeZoneIdSchema,
    locationScope: accountSummaryResolvedLocationScopeSchema,
    alert: bzOptional(accountSummaryAlertSchema),
    narrative: bzOptional(accountSummaryNarrativeSchema),
    accountIdentity: accountSummaryIdentitySchema,
    contacts: z.array(accountSummaryContactSchema),
    notes: accountSummaryNotesSchema,
    maintenancePlans: accountSummaryMaintenancePlansSectionSchema,
    locations: z.array(accountSummaryLocationSchema),
    serviceHistory: accountSummaryServiceHistorySectionSchema,
    invoiceHistory: accountSummaryInvoiceHistorySectionSchema,
    paymentSummary: accountSummaryPaymentSummarySchema,
    timeline: accountSummaryTimelineSectionSchema,
  })
  .openapi('CreateAccountSummaryPdfBody')

export const buildAccountSummaryScopeLabels = (args: {
  type: 'ALL' | 'SELECTED'
  selectedLocations: Array<{ displayLabel: string }>
  totalLocationCount: number
}): {
  shortLabel: string
  detailLabel?: string
  selectedLocationCount: number
} => {
  if (args.type === LOCATION_SCOPE_ALL) {
    return {
      shortLabel: 'All Locations',
      selectedLocationCount: args.totalLocationCount,
    }
  }

  const selectedLocationCount = args.selectedLocations.length
  const detailLabel =
    selectedLocationCount <= 3
      ? `Selected Locations: ${args.selectedLocations.map(location => location.displayLabel).join(', ')}`
      : `Selected Locations (${selectedLocationCount} of ${args.totalLocationCount})`

  return {
    shortLabel: 'Selected Locations',
    detailLabel,
    selectedLocationCount,
  }
}

export const normalizeAccountSummarySelectedLocationGuids = (locationGuids: string[]): string[] => {
  return Array.from(new Set(locationGuids)).sort()
}

export type AccountSummaryLocationSelection = z.infer<typeof accountSummaryLocationSelectionSchema>
export type AccountSummaryLocationScopeInput = z.infer<typeof accountSummaryLocationScopeInputSchema>
export type GetAccountSummaryPdfLocationOptionsInput = z.infer<typeof getAccountSummaryPdfLocationOptionsInputSchema>
export type GetAccountSummaryPdfLocationOptionsOutput = z.infer<typeof getAccountSummaryPdfLocationOptionsOutputSchema>
export type DownloadAccountSummaryPdfInput = z.infer<typeof downloadAccountSummaryPdfInputSchema>
export type AccountSummaryPdfResponse = z.infer<typeof accountSummaryPdfResponseSchema>
export type AccountSummaryResolvedLocationScope = z.infer<typeof accountSummaryResolvedLocationScopeSchema>
export type AccountSummaryAlert = z.infer<typeof accountSummaryAlertSchema>
export type AccountSummaryNarrative = z.infer<typeof accountSummaryNarrativeSchema>
export type AccountSummaryIdentity = z.infer<typeof accountSummaryIdentitySchema>
export type AccountSummaryContact = z.infer<typeof accountSummaryContactSchema>
export type AccountSummaryPinnedNote = z.infer<typeof accountSummaryPinnedNoteSchema>
export type AccountSummaryNotes = z.infer<typeof accountSummaryNotesSchema>
export type AccountSummaryMaintenancePlan = z.infer<typeof accountSummaryMaintenancePlanSchema>
export type AccountSummaryMaintenancePlansSection = z.infer<typeof accountSummaryMaintenancePlansSectionSchema>
export type AccountSummaryEquipment = z.infer<typeof accountSummaryEquipmentSchema>
export type AccountSummaryHvacSystem = z.infer<typeof accountSummaryHvacSystemSchema>
export type AccountSummaryLocation = z.infer<typeof accountSummaryLocationSchema>
export type AccountSummaryServiceHistorySection = z.infer<typeof accountSummaryServiceHistorySectionSchema>
export type AccountSummaryInvoiceHistorySection = z.infer<typeof accountSummaryInvoiceHistorySectionSchema>
export type AccountSummaryPaymentSummary = z.infer<typeof accountSummaryPaymentSummarySchema>
export type AccountSummaryTimelineSection = z.infer<typeof accountSummaryTimelineSectionSchema>
export type CreateAccountSummaryPdfBody = z.infer<typeof CreateAccountSummaryPdfBodySchema>
